// External Packages
import PropTypes from 'prop-types';
import { Component } from 'react';

// Internal Packages
import { checkGALoadedPromise } from 'utils/googleAnalytics';
import { getGoBaseUrl, isTrackingDisabled } from 'utils/url';
import { isInProduct } from 'utils/inProduct';
import logger from 'utils/rollbar';

// Local Functions
function getAuthorSlug() {
  const authorLinkElement = document.querySelector('link[rel="author"][href]');
  if (!authorLinkElement) {
    return '';
  }
  const authorSlug = authorLinkElement.getAttribute('href').split('/').pop();
  return authorSlug || '';
}

function sendSpaNavigationData() {
  const authorSlug = getAuthorSlug();

  window.dataLayer.push({
    author: authorSlug,
    event: 'pageview',
  });
}

// Component Definition
class MarketingTracker extends Component {
  componentDidMount() {
    if (!isTrackingDisabled && !isInProduct(window.location.search)) {
      window.requestAnimationFrame(() => {
        this.initTracking();
        this.checkGALoadedPromise = checkGALoadedPromise();
      });
    }
  }

  componentDidUpdate(previousProps) {
    if (!isTrackingDisabled && !isInProduct(window.location.search)) {
      // Detect when the loading spinner disappears (meaning content is ready)
      const isUncachedPageLoad =
        previousProps.isContentLoading &&
        !this.props.isContentLoading;

      // Detect when a page cached in the Redux store is being re-visited (isContentLoading is never true)
      const isReduxCachedPageLoad =
        !previousProps.isContentLoading &&
        !this.props.isContentLoading &&
        previousProps.location.pathname !== this.props.location.pathname;

      if (isUncachedPageLoad || isReduxCachedPageLoad) {
        this.checkGALoadedPromise
          .then(() => window.requestAnimationFrame(() => sendSpaNavigationData()))
          .catch((error) => logger.warning('Google Analytics was not loaded when it should be.', error));
      }
    }
  }

  initTracking = () => {
    const { magazineEditionLanguage } = this.props;

    return import('@lessonnine/marketing-tracker').then(async (marketingTracker) => {
      // Add page's author name to Google Tag Manager dataLayer queue or empty string if no author
      const authorSlug = getAuthorSlug();
      window.dataLayer = [{ author: authorSlug }];
      try {
        // Load Google Tag Manager
        const googleTagManager = marketingTracker.gtm;
        googleTagManager.load();
        await googleTagManager.init({
          locale: magazineEditionLanguage,
        });
        const campaignsTracker = new marketingTracker.CampaignsTracker({
        // Babbel Campaigns App ("host" really refers to the expected URL prefix, not the hostname)
          host: getGoBaseUrl(),
        });
        campaignsTracker.trackUserClick();
      } catch (error) {
        if ('Page is private so GTM will not be called from this page' !== error.message) {
          throw error;
        }
      }
    });
  };

  render() {
    return null;
  }
}

// Component Metadata
MarketingTracker.propTypes = {
  isContentLoading: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  magazineEditionLanguage: PropTypes.string.isRequired,
};

export default MarketingTracker;
